/**
 * TemplateRulesPage — Sprint 5.6 (Templates Automáticos por Perfil).
 *
 * Aba "Regras" no NOC. Gerencia as regras que sugerem o template de
 * configuração a partir do perfil do cliente/contrato.
 *
 *  - Cards: Regras ativas · Regras inativas · Perfis · Templates
 *    vinculados.
 *  - Tabela: Nome · Perfil · Template · Prioridade · Ativa · Cidade ·
 *    Plano · Tipo cliente · Tipo contrato.
 *  - Ações: Nova regra · Editar · Ativar/desativar · Excluir.
 *
 * Polling de 20s.
 */
import { useCallback, useEffect, useState } from "react";
import {
  createTemplateRule,
  deleteTemplateRule,
  listTemplateRules,
  updateTemplateRule,
} from "../lib/api.js";
import { t } from "../i18n/index.js";

const POLL_MS = 20000;

const PROFILES = ["residential", "business", "vip", "critical"];

const PROFILE_STYLE = {
  residential: "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
  business: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
  vip: "text-noc-purple bg-noc-purple/10 border-noc-purple/30",
  critical: "text-noc-err bg-noc-err/10 border-noc-err/30",
};

function ProfilePill({ profile }) {
  const cls = PROFILE_STYLE[profile] || "text-noc-muted bg-noc-bg/40";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>
      {t("rules.profile_" + profile) || profile}
    </span>
  );
}

export default function TemplateRulesPage() {
  const [data, setData] = useState({
    rules: [], total: 0, enabled_count: 0, disabled_count: 0,
    profiles: {}, templates_linked: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [modal, setModal] = useState(null); // 'new' | 'edit'
  const [editRule, setEditRule] = useState(null);

  const load = useCallback((signal) => {
    listTemplateRules({ signal })
      .then((d) => {
        setData(d);
        setErr(null);
      })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const reload = () => {
    const ac = new AbortController();
    load(ac.signal);
  };

  async function toggleEnabled(rule) {
    try {
      await updateTemplateRule(rule.id, { enabled: !rule.enabled });
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  async function removeRule(rule) {
    if (!confirm(t("rules.confirmDelete"))) return;
    try {
      await deleteTemplateRule(rule.id);
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  const profileCount = Object.values(data.profiles || {})
    .filter((n) => n > 0).length;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label mb-2">Configuração automática</div>
          <h1 className="text-3xl font-semibold tracking-tight text-noc-text">
            {t("rules.title")}
          </h1>
          <p className="text-sm text-noc-muted mt-1">
            Defina critérios para selecionar e aplicar templates automaticamente.
          </p>
        </div>
        <button className="btn-primary h-10 px-4" onClick={() => setModal("new")}>
          {t("rules.btnNew")}
        </button>
      </header>

      {/* Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <DashCard label={t("rules.cardEnabled")}
                  value={data.enabled_count} tone="emerald" />
        <DashCard label={t("rules.cardDisabled")}
                  value={data.disabled_count} tone="gray" />
        <DashCard label={t("rules.cardProfiles")}
                  value={profileCount} tone="purple" />
        <DashCard label={t("rules.cardTemplatesLinked")}
                  value={data.templates_linked} tone="cyan" />
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">
          {err}
        </div>
      )}

      {/* Tabela */}
      <div className="glass-card !p-0 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-[11px] uppercase tracking-[0.12em] text-noc-muted bg-noc-bg/30 border-b border-noc-divider">
            <tr>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colName")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colProfile")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colTemplate")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colPriority")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colEnabled")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colCity")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colPlan")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colCustomerType")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colContractType")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("rules.colActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={10} className="text-sm text-noc-muted/70
                italic py-3 px-2">{t("common.loading")}</td></tr>
            )}
            {!loading && data.rules.length === 0 && (
              <tr><td colSpan={10} className="text-sm text-noc-muted/60
                italic py-3 px-2">{t("rules.empty")}</td></tr>
            )}
            {data.rules.map((r) => (
              <tr key={r.id}
                  className={"border-b border-noc-border/40 hover:bg-noc-bg/30 "
                    + (r.enabled ? "" : "opacity-50")}>
                <td className="py-1.5 px-2 text-noc-text font-medium">
                  {r.name}
                </td>
                <td className="py-1.5 px-2">
                  <ProfilePill profile={r.profile} />
                </td>
                <td className="py-1.5 px-2 text-noc-muted/80">
                  {r.template_id ? "✓" : "—"}
                </td>
                <td className="py-1.5 px-2">{r.priority}</td>
                <td className="py-1.5 px-2">
                  {r.enabled
                    ? <span className="text-noc-ok">●</span>
                    : <span className="text-noc-muted">○</span>}
                </td>
                <td className="py-1.5 px-2">{r.match_city || "—"}</td>
                <td className="py-1.5 px-2">{r.match_plan_name || "—"}</td>
                <td className="py-1.5 px-2">
                  {r.match_customer_type || "—"}
                </td>
                <td className="py-1.5 px-2">
                  {r.match_contract_type || "—"}
                </td>
                <td className="py-1.5 px-2">
                  <div className="flex gap-1">
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-border hover:bg-noc-bg/50"
                      onClick={() => { setEditRule(r); setModal("edit"); }}
                    >
                      {t("rules.btnEdit")}
                    </button>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-border hover:bg-noc-bg/50"
                      onClick={() => toggleEnabled(r)}
                    >
                      {r.enabled
                        ? t("rules.btnDisable")
                        : t("rules.btnEnable")}
                    </button>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-err/30 text-noc-err
                                 hover:bg-noc-err/10"
                      onClick={() => removeRule(r)}
                    >
                      {t("rules.btnDelete")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-noc-muted/70">
        {t("rules.total")}: {data.total}
      </div>

      {modal === "new" && (
        <RuleFormModal
          title={t("rules.btnNew")}
          onClose={() => setModal(null)}
          onSubmit={async (body) => {
            await createTemplateRule(body);
            setModal(null);
            reload();
          }}
        />
      )}
      {modal === "edit" && editRule && (
        <RuleFormModal
          title={t("rules.btnEdit")}
          initial={editRule}
          onClose={() => { setModal(null); setEditRule(null); }}
          onSubmit={async (body) => {
            await updateTemplateRule(editRule.id, body);
            setModal(null);
            setEditRule(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function DashCard({ label, value, tone }) {
  const toneCls = {
    emerald: "border-noc-ok/30 text-noc-ok",
    gray: "border-noc-border text-noc-muted",
    purple: "border-noc-purple/30 text-noc-purple",
    cyan: "border-noc-accent/30 text-noc-accent",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`stat-card ${toneCls}`}>
      <div className="text-2xl font-semibold mt-2">{value}</div>
      <div className="section-label">{label}</div>
    </div>
  );
}

/**
 * Modal de criação/edição de regra. Texto livre nos critérios de
 * match; perfil é um <select>. Sem <form>; submit por botão.
 */
function RuleFormModal({ title, initial, onClose, onSubmit }) {
  const TEXT_FIELDS = [
    "name", "description", "match_city", "match_plan_name",
    "match_customer_type", "match_contract_type",
  ];
  const [values, setValues] = useState(() => {
    const v = {};
    for (const f of TEXT_FIELDS) v[f] = (initial && initial[f]) || "";
    v.profile = (initial && initial.profile) || "residential";
    v.priority = initial && initial.priority != null
      ? String(initial.priority) : "0";
    return v;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const setField = (k, val) =>
    setValues((prev) => ({ ...prev, [k]: val }));

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const body = { profile: values.profile };
      for (const f of TEXT_FIELDS) {
        if (values[f] !== "") body[f] = values[f];
      }
      body.priority = Number(values.priority) || 0;
      await onSubmit(body);
    } catch (e) {
      setError(e?.message || String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4"
         onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-md space-y-3 max-h-[90vh]
                      overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">{title}</h3>
        <div className="space-y-2">
          <div>
            <label className="text-[11px] text-noc-muted block mb-0.5">
              {t("rules.field_name")}
            </label>
            <input
              value={values.name}
              onChange={(e) => setField("name", e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border
                         border-noc-border rounded px-2 py-1 text-noc-text"
            />
          </div>
          <div>
            <label className="text-[11px] text-noc-muted block mb-0.5">
              {t("rules.field_profile")}
            </label>
            <select
              value={values.profile}
              onChange={(e) => setField("profile", e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border
                         border-noc-border rounded px-2 py-1 text-noc-text"
            >
              {PROFILES.map((p) => (
                <option key={p} value={p}>
                  {t("rules.profile_" + p)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-noc-muted block mb-0.5">
              {t("rules.field_priority")}
            </label>
            <input
              type="number"
              value={values.priority}
              onChange={(e) => setField("priority", e.target.value)}
              className="w-full text-xs bg-noc-bg/60 border
                         border-noc-border rounded px-2 py-1 text-noc-text"
            />
          </div>
          {["match_city", "match_plan_name", "match_customer_type",
            "match_contract_type", "description"].map((f) => (
            <div key={f}>
              <label className="text-[11px] text-noc-muted block mb-0.5">
                {t("rules.field_" + f)}
              </label>
              <input
                value={values[f]}
                onChange={(e) => setField(f, e.target.value)}
                className="w-full text-xs bg-noc-bg/60 border
                           border-noc-border rounded px-2 py-1 text-noc-text"
              />
            </div>
          ))}
        </div>
        {error && (
          <div className="text-[11px] text-noc-err bg-noc-err/10
                          border border-noc-err/30 rounded p-1.5">
            {error}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-accent/30
                       text-noc-accent bg-noc-accent/10 hover:bg-noc-accent/10
                       disabled:opacity-50"
            disabled={busy}
            onClick={submit}
          >
            {busy ? t("common.loading") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}
